import { useCallback, useEffect, useMemo, useState } from 'react';
import { endOfMonth, format, isToday, startOfMonth } from 'date-fns';
import { hrApi } from '@/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  hrDateKey,
  hrDayTypeBackground,
  hrFormatMinutes,
  type HrResolvedDayType,
} from '@/modules/hr/components/hrCalendarUtils';
import type { HrCompanyHoliday } from '@/types/hr';
import { cn } from '@/utils/cn';

export type ResolvedDayInfo = {
  dayType: HrResolvedDayType;
  scheduledWorkDay: boolean;
  halfDay: boolean;
  expectedMinutes: number;
  holidayName?: string;
  overrideReason?: string;
  source?: string;
};

type Options = {
  viewDate: Date;
  employeeIds: number[];
  enabled?: boolean;
};

function stableEmployeeIdsKey(employeeIds: number[]): string {
  return [...new Set(employeeIds.filter((id) => id > 0))].sort((a, b) => a - b).join(',');
}

function parseEmployeeIdsKey(key: string): number[] {
  if (!key) return [];
  return key.split(',').map(Number);
}

export function useEmployeeScheduleCalendar({ viewDate, employeeIds, enabled = true }: Options) {
  const companyId = useAppSelector((s) => s.app.selectedCompany?.id);
  const monthKey = useMemo(
    () => `${viewDate.getFullYear()}-${viewDate.getMonth()}`,
    [viewDate],
  );
  const rangeStart = useMemo(() => format(startOfMonth(viewDate), 'yyyy-MM-dd'), [monthKey]);
  const rangeEnd = useMemo(() => format(endOfMonth(viewDate), 'yyyy-MM-dd'), [monthKey]);
  const employeeIdsKey = stableEmployeeIdsKey(employeeIds);
  const uniqueEmployeeIds = useMemo(() => parseEmployeeIdsKey(employeeIdsKey), [employeeIdsKey]);

  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState<HrCompanyHoliday[]>([]);
  const [resolvedByEmployee, setResolvedByEmployee] = useState<Map<number, Map<string, ResolvedDayInfo>>>(
    () => new Map(),
  );
  const reload = useCallback(async () => {
    if (!enabled || !employeeIdsKey) {
      setHolidays([]);
      setResolvedByEmployee(new Map());
      setLoading(false);
      return;
    }

    const ids = parseEmployeeIdsKey(employeeIdsKey);

    setLoading(true);
    try {
      const [holidayRows, resolvedRows] = await Promise.all([
        hrApi.listCompanyHolidays(rangeStart, rangeEnd).catch(() => [] as HrCompanyHoliday[]),
        hrApi.resolvedCalendarBatch(ids, rangeStart, rangeEnd),
      ]);

      setHolidays(holidayRows);

      const map = new Map<number, Map<string, ResolvedDayInfo>>();
      for (const row of resolvedRows) {
        const byDay = new Map<string, ResolvedDayInfo>();
        for (const day of row.days) {
          byDay.set(day.date.slice(0, 10), {
            dayType: (day.dayType as HrResolvedDayType) || 'WORKING',
            scheduledWorkDay: day.scheduledWorkDay,
            halfDay: day.halfDay,
            expectedMinutes: day.expectedMinutes,
            holidayName: day.holidayName,
            overrideReason: day.overrideReason,
            source: day.source,
          });
        }
        map.set(row.employeeId, byDay);
      }
      setResolvedByEmployee(map);
    } finally {
      setLoading(false);
    }
  }, [companyId, employeeIdsKey, enabled, rangeEnd, rangeStart]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const getResolvedDay = useCallback(
    (employeeId: number, day: Date | string): ResolvedDayInfo | undefined => {
      const key = typeof day === 'string' ? day.slice(0, 10) : hrDateKey(day);
      return resolvedByEmployee.get(employeeId)?.get(key);
    },
    [resolvedByEmployee],
  );

  const getCellBackgroundClass = useCallback(
    (employeeId: number, day: Date): string => {
      const resolved = getResolvedDay(employeeId, day);
      const today = isToday(day);
      if (!resolved) {
        return today ? 'bg-primary-50/50 dark:bg-primary-950/20' : '';
      }
      return cn(hrDayTypeBackground(resolved.dayType), today && 'ring-1 ring-inset ring-primary-300 dark:ring-primary-700');
    },
    [getResolvedDay],
  );

  const getHeaderBackgroundClass = useCallback(
    (day: Date, employeeId?: number): string => {
      const resolved = employeeId != null ? getResolvedDay(employeeId, day) : undefined;
      const sample =
        resolved ??
        (uniqueEmployeeIds.length > 0 ? getResolvedDay(uniqueEmployeeIds[0], day) : undefined);
      const today = isToday(day);
      if (!sample) {
        return today ? 'bg-primary-50 dark:bg-primary-950/30' : '';
      }
      return cn(
        hrDayTypeBackground(sample.dayType),
        today && 'ring-1 ring-inset ring-primary-300 dark:ring-primary-700',
      );
    },
    [getResolvedDay, uniqueEmployeeIds],
  );

  const getDayTooltip = useCallback(
    (employeeId: number, day: Date): string | undefined => {
      const resolved = getResolvedDay(employeeId, day);
      if (!resolved) return undefined;
      const parts = [resolved.dayType.replace('_', ' ')];
      if (resolved.holidayName) parts.push(resolved.holidayName);
      if (resolved.overrideReason) parts.push(resolved.overrideReason);
      if (resolved.expectedMinutes > 0) {
        parts.push(`${hrFormatMinutes(resolved.expectedMinutes)} expected`);
      }
      return parts.join(' · ');
    },
    [getResolvedDay],
  );

  const holidayDates = useMemo(
    () => new Set(holidays.map((h) => h.holidayDate.slice(0, 10))),
    [holidays],
  );

  return {
    loading,
    holidays,
    holidayDates,
    getResolvedDay,
    getCellBackgroundClass,
    getHeaderBackgroundClass,
    getDayTooltip,
    reload,
  };
}
